import { Link } from 'react-router-dom'
import type { DashboardPeriodState } from '../useDashboardPeriod'
import { ModalShell } from './ModalShell'
import { useManagerDetail } from './useDrilldown'

function formatRub(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `₽ ${(n / 1_000_000).toFixed(1)}M`
  if (Math.abs(n) >= 1_000) return `₽ ${Math.round(n / 1_000)}K`
  return `₽ ${Math.round(n)}`
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

export function ManagerDetailModal({
  managerId,
  period,
  onClose,
}: {
  managerId: string
  period: DashboardPeriodState
  onClose: () => void
}) {
  const { data, isLoading, isError } = useManagerDetail(managerId, period)

  return (
    <ModalShell
      title={data?.name ?? 'Менеджер'}
      subtitle={data ? `${data.email} · аналитика за период` : undefined}
      onClose={onClose}
      size="lg"
    >
      {isLoading && <p className="text-sm text-gray-400">Загрузка…</p>}
      {isError && <p className="text-sm text-red-600">Не удалось загрузить данные</p>}
      {data && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Заявок" value={data.stats.requestsCount} />
            <Stat label="Сумма КП" value={formatRub(data.stats.kpSumRub)} />
            <Stat
              label="Ср. время до КП"
              value={data.stats.avgTimeToKpDays === null ? '—' : `${data.stats.avgTimeToKpDays}д`}
            />
            <Stat
              label="Конверсия"
              value={
                data.stats.conversionPercent === null ? '—' : `${data.stats.conversionPercent}%`
              }
            />
          </div>

          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
              Последние заявки
            </div>
            {data.recentRequests.length === 0 ? (
              <p className="text-sm text-gray-400">Нет заявок за период</p>
            ) : (
              <div className="divide-y divide-gray-100 rounded-xl border border-gray-100">
                {data.recentRequests.slice(0, 10).map((r) => (
                  <Link
                    key={r.id}
                    to={`/requests/${r.id}`}
                    onClick={onClose}
                    className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                  >
                    <span className="font-mono text-xs text-gray-500 w-16 shrink-0">
                      {r.requestNumber ?? '—'}
                    </span>
                    <span className="flex-1 truncate text-gray-800">{r.clientName}</span>
                    <span className="text-xs text-gray-500 tabular-nums shrink-0">
                      {formatDate(r.createdAt)}
                    </span>
                    <span className="text-xs text-gray-700 tabular-nums w-20 text-right shrink-0">
                      {r.kpSumRub > 0 ? formatRub(r.kpSumRub) : '—'}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {data.topClients.length > 0 && (
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Топ клиентов</div>
              <div className="flex flex-wrap gap-2">
                {data.topClients.map((c) => (
                  <Link
                    key={c.id}
                    to={`/clients/${c.id}`}
                    onClick={onClose}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-50 border border-gray-200 text-xs hover:bg-gray-100"
                  >
                    <span className="text-gray-800">{c.name}</span>
                    <span className="text-gray-400 tabular-nums">{c.requestsCount}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </ModalShell>
  )
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-xl font-semibold text-gray-900 mt-0.5">{value}</div>
    </div>
  )
}
